import { userManagementApis } from "@services/apis/UserManagement";
import {
  AuthorizationSettings,
  GetSecuritySettingsResponse,
  SecuritySettingData,
} from "@services/models/UserManagement/get-security-settings";
import { UpdateSecuritySettingRequest } from "@services/models/UserManagement/update-security-settings";
import React, { useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";
import SettingsTableRow from "./SettingsTableRow";

interface SecurityGeneralProps {
  userId: number;
  settingsData: SecuritySettingData[] | undefined;
  loading: boolean;
  is2FaEnabled: boolean;
  setGeneralSettings: React.Dispatch<
    React.SetStateAction<GetSecuritySettingsResponse | null>
  >;
  emailOtpRequired: boolean;
  google2FaRequired: boolean;
}

const SecurityGeneral: React.FC<SecurityGeneralProps> = ({
  userId,
  settingsData,
  loading,
  is2FaEnabled,
  setGeneralSettings,
  emailOtpRequired,
  google2FaRequired,
}) => {
  const [settingIndex, setSettingsIndex] = useState<number>(-1);
  const [isEmailChanged, setIsEmailChanged] = useState<boolean>(false);
  const [isGoogle2FaChanged, setIsGoogle2FaChanged] = useState<boolean>(false);

  const { handleError } = useSnackbarMessage();

  const handleSubmit = async (
    settingFrontEndKey: string,
    emailEnabled: boolean,
    emailValue: boolean,
    google2FAValue: boolean,
  ) => {
    const data: UpdateSecuritySettingRequest = {
      userId,
      settingFrontEndKey,
      settingValue: {
        email: {
          isEnabled: emailEnabled,
          value: emailValue,
        },
        google2FA: {
          isEnabled: undefined,
          value: google2FAValue,
        },
      },
    };

    try {
      await userManagementApis.updateSecuritySettings(data);
      setGeneralSettings((prevSettings) => {
        if (!prevSettings) return null;
        const updatedSettingsData = prevSettings.data.map((setting) => {
          if (setting.frontEndKey === settingFrontEndKey) {
            // Update the relevant setting
            return {
              ...setting,
              settingValue: {
                email: {
                  isEnabled: emailEnabled,
                  value: emailValue,
                },
                google2FA: {
                  isEnabled: undefined,
                  value: google2FAValue,
                },
              },
            };
          }
          return setting;
        });
        return {
          ...prevSettings,
          data: updatedSettingsData,
        };
      });

      setIsEmailChanged(false);
      setIsGoogle2FaChanged(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "An unknown error occurred";
      handleError({ message: errorMessage });
    }
  };

  const handleEmailChange = (index: number) => {
    if (!settingsData) return;

    const setting = settingsData[index];
    const settingValue = setting.settingValue as AuthorizationSettings;

    handleSubmit(
      setting.frontEndKey,
      settingValue.email.isEnabled,
      !settingValue.email.value,
      settingValue.google2FA.value,
    );
  };

  const handleGoogle2FaChange = (index: number) => {
    if (!settingsData) return;

    const setting = settingsData[index];
    const settingValue = setting.settingValue as AuthorizationSettings;

    handleSubmit(
      setting.frontEndKey,
      settingValue.email.isEnabled,
      settingValue.email.value,
      !settingValue.google2FA.value,
    );
  };

  return (
    <div className="flex flex-col bg-bg-page-body px-[16px] pb-[16px]">
      <div className="flex flex-col mt-[16px] ">
        <table className="min-w-full text-text-base text-[12px] ">
          <thead className="bg-bg-header rounded-t-lg">
            <tr>
              <th
                className="p-[16px] text-left text-text-base text-[14px] rounded-tl-lg font-semibold"
                style={{ width: "300px" }}>
                Confirmations
              </th>
              <th className="p-[16px] text-center text-text-base text-[14px] font-semibold">
                Email
              </th>
              <th className="p-[16px] text-center text-text-base text-[14px] rounded-tr-lg font-semibold">
                2FA
              </th>
            </tr>
          </thead>
          <tbody className="min-w-full border border-border-table rounded-b-lg">
            {settingsData
              ? settingsData.map((setting, index) => {
                  const settingValue =
                    setting.settingValue as AuthorizationSettings;
                  return (
                    <SettingsTableRow
                      key={index}
                      title={setting.title}
                      email={settingValue.email.value}
                      google2Fa={settingValue.google2FA.value}
                      rounded={index === settingsData.length - 1}
                      onEmailChange={() => handleEmailChange(index)}
                      onGoogle2FaChange={() => handleGoogle2FaChange(index)}
                      isEmailEnabled={settingValue.email.isEnabled}
                      isGoogle2FaEnabled={is2FaEnabled}
                      loading={loading}
                    />
                  );
                })
              : [...Array(4)].map((_, index) => (
                  <SettingsTableRow
                    key={index}
                    title=""
                    email={false}
                    google2Fa={false}
                    rounded={index === 3}
                    isEmailEnabled={false}
                    onEmailChange={() => {}}
                    onGoogle2FaChange={() => {}}
                    isGoogle2FaEnabled={false}
                    loading={true}
                  />
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SecurityGeneral;
